import { useRef, useState, type DragEvent } from 'react';
import { PlusIcon, Square2StackIcon, XMarkIcon } from '@heroicons/react/20/solid';
import BaseTooltip from '@/global/Tooltip/BaseTooltip';
import { userAppContext } from '@/context/Context';
import type { UserAppContextType } from '@/router/types';
import { useSearchTabsBar } from '@/store/searchTabsStore';
import { useSearchTabNavigation } from '@/hooks/search/useSearchTabNavigation';
import { isSearchTabsEnabled } from '@/config/searchTabs';
import './SearchBrowserTabs.css';

function buildBlankSearch(source: string): { search: string; title: string } {
  return {
    search: `?view=ALL&idents=ALL&source=${source}&tab=GDA`,
    title: 'All results',
  };
}

export function SearchBrowserTabs() {
  const { tabs, activeTabId, canOpenTab, maxTabs } = useSearchTabsBar();
  const { switchToTab, closeSearchTab, duplicateSearchTab, reorderSearchTabs, navigateSearch } =
    useSearchTabNavigation();
  const { restrictSources, groups } = userAppContext() as unknown as UserAppContextType;
  const dragFromIndexRef = useRef<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const isAcademic = groups?.includes('ROLE_ACADEMIC');
  const source = restrictSources === 'CURATED' || isAcademic ? 'CURATED' : 'ALL';

  if (!isSearchTabsEnabled || tabs.length === 0) {
    return null;
  }

  const handleOpenBlank = () => {
    if (!canOpenTab) return;
    const blank = buildBlankSearch(source);
    navigateSearch({
      search: blank.search,
      title: blank.title,
      openInNewTab: true,
    });
  };

  const handleDragStart = (index: number) => (evt: DragEvent) => {
    const target = evt.target as HTMLElement;
    if (target.closest('button.search-browser-tabs__action, button.search-browser-tabs__close')) {
      evt.preventDefault();
      return;
    }
    dragFromIndexRef.current = index;
    evt.dataTransfer.effectAllowed = 'move';
    evt.dataTransfer.setData('text/plain', String(index));
  };

  const handleDragOver = (index: number) => (evt: DragEvent) => {
    evt.preventDefault();
    evt.dataTransfer.dropEffect = 'move';
    if (dragOverIndex !== index) setDragOverIndex(index);
  };

  const handleDrop = (toIndex: number) => (evt: DragEvent) => {
    evt.preventDefault();
    const fromIndex = dragFromIndexRef.current;
    dragFromIndexRef.current = null;
    setDragOverIndex(null);
    if (fromIndex == null || fromIndex === toIndex) return;
    reorderSearchTabs(fromIndex, toIndex);
  };

  const handleDragEnd = () => {
    dragFromIndexRef.current = null;
    setDragOverIndex(null);
  };

  return (
    <div className="search-browser-tabs" data-testid="search-browser-tabs">
      <div className="search-browser-tabs__list" role="tablist" aria-label="Search tabs">
        {tabs.map((tab, index) => {
          const isActive = tab.id === activeTabId;
          const isDropTarget = dragOverIndex === index;
          return (
            <div
              key={tab.id}
              className={`search-browser-tabs__tab ${isActive ? 'is-active' : ''}${isDropTarget ? ' is-drop-target' : ''}`}
              role="presentation"
              draggable
              onDragStart={handleDragStart(index)}
              onDragOver={handleDragOver(index)}
              onDrop={handleDrop(index)}
              onDragEnd={handleDragEnd}
            >
              <button
                type="button"
                role="tab"
                aria-selected={isActive}
                id={`search-tab-${tab.id}`}
                className="search-browser-tabs__tab-button"
                title={tab.title}
                onClick={() => {
                  if (!isActive) switchToTab(tab.id);
                }}
              >
                <span className="search-browser-tabs__tab-title">{tab.title}</span>
              </button>
              <BaseTooltip
                content={canOpenTab ? 'Duplicate tab' : `Maximum of ${maxTabs} tabs reached`}
                position="bottom"
              >
                <button
                  type="button"
                  className="search-browser-tabs__action"
                  aria-label={`Duplicate tab ${tab.title}`}
                  disabled={!canOpenTab}
                  onClick={evt => {
                    evt.stopPropagation();
                    duplicateSearchTab(tab.id);
                  }}
                >
                  <Square2StackIcon className="h-3 w-3" aria-hidden />
                </button>
              </BaseTooltip>
              <button
                type="button"
                className="search-browser-tabs__close"
                aria-label={`Close tab ${tab.title}`}
                title="Close tab"
                onClick={evt => {
                  evt.stopPropagation();
                  closeSearchTab(tab.id, () => buildBlankSearch(source));
                }}
              >
                <XMarkIcon className="h-3.5 w-3.5" aria-hidden />
              </button>
            </div>
          );
        })}
      </div>

      <BaseTooltip
        content={canOpenTab ? 'New search tab' : `Maximum of ${maxTabs} tabs reached`}
        position="bottom"
      >
        <button
          type="button"
          className="search-browser-tabs__add"
          aria-label="Open new search tab"
          disabled={!canOpenTab}
          onClick={handleOpenBlank}
          data-testid="search-browser-tabs-add"
        >
          <PlusIcon className="h-4 w-4" aria-hidden />
        </button>
      </BaseTooltip>
    </div>
  );
}

export default SearchBrowserTabs;
